/**
 * $ELYXR, as the site talks about it.
 *
 * One switch drives every surface: the announcement bar, the contract pill in
 * the header, and the block on the token page. With `CONTRACT` null they all
 * read "launching soon" and carry no chart link; with it set they show the
 * address and wire the links. The copy cannot get ahead of the deployment,
 * because the same constant decides both.
 *
 * $ELYXR is on **Robinhood Chain** (4663), not Ethereum. The treasury also
 * takes deposits there now, but the two are unrelated: this file is about the
 * token, and `apps/api` settlement is about custody.
 */

/** The token contract. This is the address people verify and copy. */
const CONTRACT: string | null = '0x0E9320e77426b45C48117f1587d7Ce96185A1AD8'

/**
 * The DexScreener page, stored whole rather than assembled from `CONTRACT`.
 *
 * Deliberate, and the reason this is not a derived URL: a DexScreener link
 * addresses the **liquidity pool**, not the token, and a pool address cannot
 * be computed from a token address — it falls out of whatever factory and
 * pairing created it. The first version of this file built the URL from the
 * contract plus a chain slug, which would have shipped a confident link to a
 * page that does not exist.
 *
 * Null until there is a pool to point at.
 */
const DEXSCREENER_URL: string | null =
  'https://dexscreener.com/robinhood/0x3bbe11b1c215a4a4372f2fc620b600d753cb8da4'

/**
 * Robinhood Chain's explorer. Blockscout, not Etherscan — the token is not on
 * Ethereum, and an Etherscan link would 404 on an address that really exists,
 * which is worse than no link at all.
 */
const EXPLORER_BASE = 'https://robinhoodchain.blockscout.com'

/**
 * Typed rather than `as const`: a literal type would narrow `contract` to its
 * exact value and the guards below would stop type-checking against the other
 * state. Declaring it `string | null` keeps both branches valid.
 */
interface Token {
  symbol: string
  /** `$ELYXR`, for the many places that want the sigil. */
  ticker: string
  contract: string | null
  /** Human-readable, for the "on Robinhood Chain" in the copy. */
  chainName: string
}

export const TOKEN: Token = {
  symbol: 'ELYXR',
  ticker: '$ELYXR',
  contract: CONTRACT,
  chainName: 'Robinhood Chain',
}

/** True only once there is an address to show and to link to. */
export function isTokenLive(): boolean {
  return TOKEN.contract !== null && TOKEN.contract.length > 0
}

/** The chart, or null while there is no pool to chart. */
export function dexscreenerUrl(): string | null {
  return DEXSCREENER_URL
}

/** The token's explorer page, or null pre-launch. */
export function explorerUrl(): string | null {
  return isTokenLive() ? `${EXPLORER_BASE}/token/${TOKEN.contract}` : null
}

/**
 * The address, shortened for display. Longer head than `shortAddress` in
 * format.ts on purpose: a contract address is something people verify against
 * a listing before they trade, so it shows enough to actually be checked.
 */
export function shortContract(): string | null {
  const c = TOKEN.contract
  if (!c) return null
  return `${c.slice(0, 10)}…${c.slice(-8)}`
}
